import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-context';
import { useT } from '../i18n/locale-context';
import { listSavedSearches } from '../lib/saved-search-client';
import { AuthApiError } from '../lib/auth-client';
import { TelegramConnectButton } from './telegram-connect-button';
import type { TelegramBotAuthResult } from './telegram-bot-login';
import {
  companySkipRemembered,
  companyStepDone,
  orderedSteps,
  type OnboardingStepId,
} from '../lib/onboarding-steps';
import { CheckIcon } from './icons';
import { CompanyInnModal } from './company-inn-modal';
import { readGate, writeGate } from '../lib/onboarding-gate';
import { VerifyEmailAction } from './verify-email-action';

/**
 * A slim, always-present progress strip on the catalog while onboarding is
 * unfinished. Collapsible, deliberately NOT closable.
 *
 * The modal gets three showings and then stops for good (see onboarding-gate),
 * which leaves a gap: a user who dismissed it three times has no remaining path
 * back to Telegram alerts or the ИНН step except by stumbling on them. This is
 * that path — one line, always available, never interrupting.
 *
 * ## Why "not closable" is defensible here, and where the line is
 *
 * It clears by being COMPLETED, not by being dismissed, and completion is
 * cheap: save a search, connect Telegram. That is a reminder.
 *
 * It would be a dark pattern if any step were impossible for a legitimate user
 * — and one is. Plenty of people have no ИНН at all (individuals researching the
 * market, employees of a company registered to someone else), so an uncloseable
 * "add your ИНН" would nag them forever with no possible resolution. Hence
 * «Позже», which retires that step from the REMINDERS — phrased about timing
 * rather than the person, so nobody has to declare anything about their own
 * legal status to get past it, and it stays literally true: the ИНН form is a
 * permanent, non-dismissible card in settings, so "later" remains available
 * forever. The rule it follows: a persistent prompt must always have a
 * reachable end state for every user it can appear to.
 *
 * Collapsed by default after the first collapse, so it costs one line of
 * vertical space on a mobile catalog — 99% of traffic is mobile.
 *
 * ⚠️ ON A PHONE IT IS ONE LINE BY DEFAULT (Igor, 10.10, Redmi 720×1600): right
 * after onboarding the expanded list filled the whole first screen of the
 * catalogue and not one lot was visible without scrolling. Below `sm` the
 * steps now open only on a tap of «Показать» (per visit, not remembered);
 * `sm` and up keep the expanded-unless-collapsed default. CSS decides which,
 * not a width probe, so the prerendered HTML is the same for both.
 */
const TG_BOT = import.meta.env.VITE_TELEGRAM_BOT_USERNAME as string | undefined;

export function OnboardingBanner() {
  const { user, status, completeTelegramBotLogin } = useAuth();
  const t = useT();
  const [gate, setGate] = useState(readGate);
  // Expanded unless the user collapsed it before. Collapsed-by-default meant the
  // steps were invisible to anyone who did not think to tap a one-line strip.
  const expanded = !gate.bannerCollapsed;
  /** Phone only: the reader tapped «Показать» on this visit. */
  const [phoneOpen, setPhoneOpen] = useState(false);
  const toggle = () => {
    // Which half of the control was tapped is a layout fact, read at the tap —
    // never at render (that would split the prerender from the client).
    const phone =
      typeof window !== 'undefined' && !window.matchMedia?.('(min-width: 640px)').matches;
    if (phone) {
      setPhoneOpen((o) => !o);
      return;
    }
    const next = { ...gate, bannerCollapsed: expanded };
    writeGate(next);
    setGate(next);
  };
  const [tgError, setTgError] = useState<string | null>(null);
  const [innOpen, setInnOpen] = useState(false);

  /**
   * Connect Telegram HERE rather than linking somewhere.
   *
   * The first version pointed at `/app/settings`, which does not exist — a 404.
   * And any "go there and do it" hop is a detour around the one control that
   * does the work: since 2026-10-18 the bot button right in this row (Start in
   * the bot links Telegram to this session).
   */
  function showTgError(e: unknown) {
    setTgError(
      e instanceof AuthApiError && e.code === 'telegram_account_taken'
        ? t('telegram.connect.taken')
        : t('telegram.connect.error'),
    );
  }

  /** Start in the bot linked Telegram server-side; re-reading the session
   *  flips this row done. */
  async function handleTelegramBot(result: TelegramBotAuthResult) {
    setTgError(null);
    await completeTelegramBotLogin(result);
  }

  const authed = status === 'authed' && !!user;
  const { data: searches } = useQuery({
    queryKey: ['saved-searches'],
    queryFn: listSavedSearches,
    enabled: authed,
  });

  if (!authed || !user) return null;

  // Telegram-native accounts are backfilled `emailVerified: false` with a
  // synthetic `telegram_<id>@users.uztender.com` address that can never
  // receive an OTP (the server's `isRealEmail` guard drops the send
  // silently, same pattern as `contact-support.tsx`) — without this check the
  // row would send the user to a code screen with no way off it. `undefined`
  // (a payload that has not said) is treated the same way: unknown is not
  // "unverified".
  const hasRealEmail = !!user.email && !/^telegram_\d+@users\.uztender\.com$/i.test(user.email);
  const emailVerified =
    hasRealEmail && typeof user.emailVerified === 'boolean' ? user.emailVerified : undefined;

  // Wait for the searches query rather than flashing "0 of 3 done" and then
  // correcting itself — a progress meter that jumps backwards reads as broken.
  if (searches === undefined) return null;

  const steps = orderedSteps({
    hasSavedSearch: searches.length > 0,
    telegramConnected: Boolean(user.telegramConnected),
    hasTin: companyStepDone(user),
    innSkipped: gate.innSkipped || companySkipRemembered(user),
    // Only this surface passes it — see the field's docblock. It makes the
    // strip «… из 4» for an account with a real, unconfirmed address.
    emailVerified,
  });

  // Every remaining step is done → nothing left for this strip to remind
  // anyone of.
  const done = steps.filter((s) => s.done).length;
  if (done === steps.length) return null;

  function skipInn() {
    const next = { ...gate, innSkipped: true };
    writeGate(next);
    setGate(next);
  }

  // Noun phrases, not imperatives — a deliberate constraint, not a style whim.
  // The Telegram row shares its width with telegram.org's iframe, which is
  // hard-coded to 148px by their own embed script, leaving ~137px for text at
  // 375px. «Подключите Telegram» wraps in that slot; «Оповещения» does not. The
  // verb lives on the adjacent control (link, button, widget) in every row, so
  // the labels read as checklist items rather than losing one to a line break.
  const label: Record<OnboardingStepId, string> = {
    search: t('onboarding.banner.search'),
    email: t('onboarding.banner.email'),
    telegram: t('onboarding.banner.telegram'),
    inn: t('onboarding.banner.inn'),
  };

  return (
    <div className="mb-3 rounded-lg border border-brand/25 bg-brand/5">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        {/* Phones get the short wording (2026-10-06, Igor: «просто короче
            текста в телефонах, чтобы помещалось и красивее выглядело») —
            same facts, fewer words; `sm` and up keep the full sentence. CSS,
            not a width probe, so the prerendered HTML is the same for both. */}
        <span className="text-[13px] font-semibold text-ink">
          <span className="sm:hidden">
            {t('onboarding.banner.titleShort', { done, total: steps.length })}
          </span>
          <span className="hidden sm:inline">
            {t('onboarding.banner.title', { done, total: steps.length })}
          </span>
        </span>
        <span className="ml-auto text-[12px] font-semibold text-brand">
          <span className="sm:hidden">
            {phoneOpen ? t('onboarding.banner.hide') : t('onboarding.banner.show')}
          </span>
          <span className="hidden sm:inline">
            {expanded ? t('onboarding.banner.hide') : t('onboarding.banner.show')}
          </span>
        </span>
      </button>

      {(expanded || phoneOpen) && (
        <ul
          className={`flex-col gap-2 border-t border-brand/15 px-3 py-2 ${
            phoneOpen ? 'flex' : 'hidden'
          } ${expanded ? 'sm:flex' : 'sm:hidden'}`}
          data-testid="onboarding-steps"
        >
          {steps.map(({ id, done: stepDone }) => (
            <li
              key={id}
              /* Compact rows on purpose — this is a one-line reminder strip, not
                 a form, and it sits above the catalog on a mobile-first page.
                 `overflow-x-hidden` is the last-resort guard against anything
                 wider than its slot stretching the whole strip.
                 ⚠️ `flex-wrap` is load-bearing, not tidiness. «Подключить
                 Telegram» measures ~185px in Chrome; on a 320px phone the row
                 has ~155px left after the tick and «Оповещения», so without a
                 wrap the CTA's right edge lands outside the row and
                 `overflow-x-hidden` shears it — measured at 320px, label right
                 edge 334 vs row right edge 291. Wrapping drops it to its own
                 line (262px of room) instead. 375px fits inline with nothing to
                 spare, so this must not be traded for a one-line row. */
              className={`flex min-h-8 flex-wrap items-center gap-x-2 gap-y-1.5 overflow-x-hidden text-[13px] ${
                stepDone ? 'text-ink-muted line-through' : 'text-ink-soft'
              }`}
            >
              {/* Same filled-tick / hollow-ring vocabulary as the dashboard
                  checklist, so a step reads identically on both surfaces. The
                  done rows are the point of showing them at all: «1 из 3» with
                  only the outstanding rows visible gives no clue what the 1 was. */}
              <span
                aria-hidden
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
                  stepDone ? 'bg-brand text-white' : 'border-[1.5px] border-edge'
                }`}
              >
                {stepDone ? <CheckIcon className="h-2.5 w-2.5" /> : null}
              </span>
              <span>{label[id]}</span>
              {id === 'email' && !stepDone && user.email && (
                <VerifyEmailAction email={user.email} />
              )}
              {id === 'inn' && !stepDone && (
                /* Opens the ask INSTEAD of navigating to it, and instead of
                   offering a dismissal here.
                   • Not a link to /app: the form is one route away, and a
                     reminder that costs a page load to act on mostly does not
                     get acted on.
                   • Not a «Позже» here: this row has space for three words,
                     which is not space to explain what is being declined. The
                     dismissal now lives inside the modal, next to the reasons.
                   The strip therefore offers exactly one thing — open it — and
                   both answers are given where the explanation is. */
                <button
                  type="button"
                  onClick={() => setInnOpen(true)}
                  className="ml-auto shrink-0 rounded-md border-[1.5px] border-brand px-2.5 py-1 text-[12px] font-bold text-brand transition-colors hover:bg-brand-dim"
                >
                  {t('onboarding.banner.activate')}
                </button>
              )}
              {id === 'telegram' && !stepDone && TG_BOT && (
                /* Our own control, in the same shape as «Указать» one row
                     down — Telegram's raw widget was never a site button here.
                     • It shipped as the bare iframe, so the row ended in
                       Telegram's «Log in with Telegram» chrome, in English, on
                       a Russian/Uzbek checklist.
                     • It was also CLIPPED on phones: this wrapper tried to undo
                       the component's mobile upscale with `[&>div]:!scale-100`,
                       but that scale is applied to the IFRAME
                       (`max-sm:[&>iframe]:scale-125`), not to a div — so below
                       `sm` a ~185px-wide widget sat in a `shrink-0` slot inside
                       an `overflow-x-hidden` row and lost its right edge.
                     Since 2026-10-18 the button opens the bot
                     (`TelegramConnectButton`); Start there links Telegram to
                     this session. The widget, and its «one per page» priority
                     table that once let this strip starve the modal, are gone. */
                <span className="ml-auto shrink-0">
                  <TelegramConnectButton
                    botUsername={TG_BOT}
                    onBotAuth={handleTelegramBot}
                    onBotError={showTgError}
                    surface="onboardingStrip"
                    compact
                  />
                </span>
              )}
            </li>
          ))}
          {tgError && (
            <li className="text-[12px] text-status-closing-text" role="alert">
              {tgError}
            </li>
          )}
        </ul>
      )}

      <CompanyInnModal
        open={innOpen}
        onClose={() => setInnOpen(false)}
        onLater={() => {
          skipInn();
          setInnOpen(false);
        }}
      />
    </div>
  );
}
